import { get } from "lodash";
import React from "react";

import Button from "antd/lib/button";
import Modal from "antd/lib/modal";
import routeWithUserSession from "@/components/application-area/routeWithUserSession";
import navigateTo from "@/components/application-area/navigateTo";
import Paginator from "@/components/items-list/components/Paginator";
import SmartContractDialog from "@/components/smart-contracts/SmartContractDialog";

import { wrap as itemsList, ControllerType } from "@/components/items-list/ItemsList";
import { ResourceItemsSource } from "@/components/items-list/classes/ItemsSource";
import { StateStorage } from "@/components/items-list/classes/StateStorage";

import LoadingState from "@/components/items-list/components/LoadingState";
import ItemsTable, { Columns } from "@/components/items-list/components/ItemsTable";
import wrapSettingsTab from "@/components/settings/SettingsWrapper";
import PlainButton from "@/components/plain-button/PlainButton";

import SmartContractService from "@/services/smart-contract";
import { currentUser } from "@/services/auth";
import { policy } from "@/services/policy";
import notification from "@/services/notification";
import routes from "@/services/routes";

import "./SmartContractsList.less";

const canEditSmartContract = smartContract => currentUser.isAdmin || currentUser.id === get(smartContract, "user.id");

const CHAINS = {
  1: "mainnet",
  56: "bsc",
  137: "polygon",
  8453: "base",
  84532: "base_sepolia",
  42161: "arbitrum",
  43114: "avalanche",
  42220: "celo",
  10: "optimism",
  421613: "arbitrum_sepolia",
  10000: "gnosis",
  100: "xdai",
};

class SmartContractsList extends React.Component {
  static propTypes = {
    controller: ControllerType.isRequired,
  };

  state = {
    dialogVisible: false,
    selectedSmartContract: null,
  };

  componentDidMount() {
    const { isNewOrEditPage, smartContractId } = this.props.controller.params;

    if (isNewOrEditPage) {
      if (smartContractId === "new") {
        if (policy.isCreateSmartContractEnabled()) {
          this.showContractDialog();
        } else {
          navigateTo("smart_contract", true);
        }
      } else {
        SmartContractService.get({ id: smartContractId })
          .then(this.showContractDialog)
          .catch(error => {
            this.props.controller.handleError(error);
          });
      }
    }
  }

  listColumns = [
    Columns.custom.sortable(
      (text, smartContract) => (
        <PlainButton type="link" className="table-main-title" onClick={() => this.showContractDialog(smartContract)}>
          {smartContract.name}
        </PlainButton>
      ),
      {
        title: "Name",
        field: "name",
        className: "text-nowrap",
      }
    ),
    Columns.custom.sortable(
      text => CHAINS[text] || text,
      {
        title: "Chain",
        field: "chain_id",
        className: "text-nowrap",
      }
    ),
    Columns.custom.sortable(text => text, {
      title: "Address",
      field: "address",
      className: "text-nowrap",
    }),
    Columns.custom.sortable(text => text, {
      title: "Deploy Block",
      field: "deploy_block",
      className: "text-nowrap",
    })
  ];

  saveSmartContract = smartContract => {
    const saveContract = smartContract.id ? SmartContractService.save : SmartContractService.create;
    return saveContract(smartContract)
      .then(savedContract => {
        notification.success("Smart Contract saved successfully.");
        return savedContract;
      })
      .catch(error => {
        console.error("Error saving smart contract:", error);
        if (error.response && error.response.status === 400) {
          const errorMessage = error.response.data.message || "Invalid smart contract data. Please check your inputs and try again.";
          notification.error(errorMessage);
        } else {
          notification.error("An error occurred while saving the smart contract. Please try again later.");
        }
        throw error;
      });
  };

  deleteSmartContract = (smartContract) => {
    Modal.confirm({
      title: "Delete Smart Contract",
      content: "Are you sure you want to delete this smart contract?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: () => {
        SmartContractService.delete(smartContract)
          .then(() => {
            notification.success("Smart Contract deleted successfully.");
            this.props.controller.update();
          })
          .catch(() => {
            notification.error("Failed deleting smart contract.");
          });
      },
    });
  };

  showContractDialog = (smartContract = null) => {
    const canEdit = !smartContract || canEditSmartContract(smartContract);
    SmartContractDialog.showModal({
      smartContract,
      readOnly: !canEdit
    })
      .onClose(updatedSmartContract => {
        if (updatedSmartContract) {
          this.saveSmartContract(updatedSmartContract)
            .then(savedContract => {
              this.props.controller.update();
              notification.success("Smart Contract saved successfully.");
              if (!smartContract) {
                navigateTo(`smart_contract/${savedContract.id}`, true);
              }
            })
            .catch(() => {
              notification.error("Failed to save Smart Contract.");
            });
        }
      })
      .onDismiss(() => {
        navigateTo("smart_contract", true);
      });
  };

  hideContractDialog = () => {
    this.setState({
      dialogVisible: false,
      selectedSmartContract: null,
    });
    navigateTo("smart_contract", true);
  };

  handleSaveSmartContract = (smartContract) => {
    this.saveSmartContract(smartContract)
      .then(() => {
        this.props.controller.update();
        this.hideContractDialog();
        notification.success("Smart Contract saved successfully.");
      })
      .catch(() => {
        notification.error("Failed to save Smart Contract.");
      });
  };

  render() {
    const { controller } = this.props;

    return (
      <div>
        <div className="m-b-15">
          <Button
            type="primary"
            onClick={() => this.showContractDialog()}
            disabled={!policy.isCreateSmartContractEnabled()}
            style={{ marginRight: 8 }}
          >
            <i className="fa fa-plus m-r-5" aria-hidden="true" />
            New Smart Contract
          </Button>
        </div>

        {!controller.isLoaded && <LoadingState className="" />}
        {controller.isLoaded && controller.isEmpty && (
          <div className="text-center">
            There are no smart contracts yet.
            {policy.isCreateSmartContractEnabled() && (
              <div className="m-t-5">
                <PlainButton type="link" onClick={() => this.showContractDialog()}>
                  Click here
                </PlainButton>{" "}
                to add one.
              </div>
            )}
          </div>
        )}
        {controller.isLoaded && !controller.isEmpty && (
          <div className="table-responsive smart-contracts-table">
            <ItemsTable
              items={controller.pageItems}
              columns={this.listColumns}
              context={this.actions}
              orderByField={controller.orderByField}
              orderByReverse={controller.orderByReverse}
              toggleSorting={controller.toggleSorting}
            />
            <Paginator
              showPageSizeSelect
              totalCount={controller.totalItemsCount}
              pageSize={controller.itemsPerPage}
              onPageSizeChange={itemsPerPage => controller.updatePagination({ itemsPerPage })}
              page={controller.page}
              onChange={page => controller.updatePagination({ page })}
            />
          </div>
        )}
      </div>
    );
  }
}

// Use SmartContractsList directly instead of the wrapped version
const SmartContractsListPage = wrapSettingsTab(
  "SmartContracts.List",
  {
    permission: "create_query",
    title: "Smart Contracts",
    path: "smart_contract",
    order: 5,
  },
  itemsList(
    SmartContractsList,
    () =>
      new ResourceItemsSource({
        isPlainList: true,
        getRequest() {
          return {};
        },
        getResource() {
          return SmartContractService.query.bind(SmartContractService);
        },
      }),
    () => new StateStorage({ orderByField: "name", itemsPerPage: 100 })
  )
);

routes.register(
  "SmartContracts.List",
  routeWithUserSession({
    path: "/smart_contract",
    title: "Smart Contracts",
    render: pageProps => <SmartContractsListPage {...pageProps} currentPage="smart_contract" />,
  })
);
routes.register(
  "SmartContracts.NewOrEdit",
  routeWithUserSession({
    path: "/smart_contract/:smartContractId",
    title: "Smart Contracts",
    render: pageProps => <SmartContractsListPage {...pageProps} currentPage="smart_contract" isNewOrEditPage />,
  })
);